import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { TaxonomyProvider } from "@/context/TaxonomyContext";
import { GenderProvider } from "@/context/GenderContext";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PendingCouponBanner from "@/components/PendingCouponBanner";
import ProtectedRoute from "@/components/ProtectedRoute";

// Heavy floating widgets — defer until idle so they never compete with LCP.
// Previously these were imported eagerly and pulled into the main bundle,
// adding ~25KB of JS that ran on every page before users could even scroll.
// NOTE: WhatsApp FAB intentionally removed (user-requested off).
const FeedbackWidget = lazy(() => import("@/components/FeedbackWidget"));
const FloatingTryOnButton = lazy(() => import("@/components/FloatingTryOnButton"));

// Home stays eager — it's the LCP route.
import Home from "@/pages/Home";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";

// ---------------------------------------------------------------------------
// Route-level code splitting — these chunks load on-demand only when their
// route is visited. Drops initial JS payload on the home/landing pages
// dramatically.
// ---------------------------------------------------------------------------
const Catalog = lazy(() => import("@/pages/Catalog"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const CustomStudio = lazy(() => import("@/pages/CustomStudio"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const Orders = lazy(() => import("@/pages/Orders"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ReferralCapture = lazy(() => import("@/pages/ReferralCapture"));
const ReferAndEarn = lazy(() => import("@/pages/ReferAndEarn"));
const About = lazy(() => import("@/pages/About"));
const CustomShop = lazy(() => import("@/pages/CustomShop"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const Account = lazy(() => import("@/pages/Account"));
const CollectionPage = lazy(() => import("@/pages/CollectionPage"));
const HeavyDuty = lazy(() => import("@/pages/HeavyDuty"));
const SharedDesign = lazy(() => import("@/pages/SharedDesign"));

// Admin bundle — heaviest, loaded only when an admin actually navigates there.
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminSizeGuides = lazy(() => import("@/pages/admin/AdminSizeGuides"));
const AdminHeavyDuty = lazy(() => import("@/pages/admin/AdminHeavyDuty"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminCollections = lazy(() => import("@/pages/admin/AdminCollections"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminShippingLabel = lazy(() => import("@/pages/admin/AdminShippingLabel"));
const AdminShipments = lazy(() => import("@/pages/admin/AdminShipments"));
const AdminInventory = lazy(() => import("@/pages/admin/AdminInventory"));
const AdminCoupons = lazy(() => import("@/pages/admin/AdminCoupons"));
const AdminDesignLibrary = lazy(() => import("@/pages/admin/AdminDesignLibrary"));
const DesignerDashboard = lazy(() => import("@/pages/DesignerDashboard"));
const SellYourDesigns = lazy(() => import("@/pages/SellYourDesigns"));
const DesignerLeaderboard = lazy(() => import("@/pages/DesignerLeaderboard"));
const Contact = lazy(() => import("@/pages/Contact"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Refund = lazy(() => import("@/pages/Refund"));
const AdminDesigners = lazy(() => import("@/pages/admin/AdminDesigners"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminReturns = lazy(() => import("@/pages/admin/AdminReturns"));
const AdminProofs = lazy(() => import("@/pages/admin/AdminProofs"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminQuoteRequests = lazy(() => import("@/pages/admin/AdminQuoteRequests"));
const AdminPartners = lazy(() => import("@/pages/admin/AdminPartners"));
const AdminHomeEditor = lazy(() => import("@/pages/admin/AdminHomeEditor"));
const AdminHeaderNav = lazy(() => import("@/pages/admin/AdminHeaderNav"));
const AdminPromoBanners = lazy(() => import("@/pages/admin/AdminPromoBanners"));
const AdminCoupleInspirations = lazy(() => import("@/pages/admin/AdminCoupleInspirations"));
const AdminFeedback = lazy(() => import("@/pages/admin/AdminFeedback"));
const AdminCoupleWearPage = lazy(() => import("@/pages/admin/AdminCoupleWearPage"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/AdminEmailTemplates"));
const AdminAbandonedCarts = lazy(() => import("@/pages/admin/AdminAbandonedCarts"));
const AdminReferrals = lazy(() => import("@/pages/admin/AdminReferrals"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("@/pages/admin/AdminUserDetail"));
const AdminNewsletter = lazy(() => import("@/pages/admin/AdminNewsletter"));
const AdminBrandAssets = lazy(() => import("@/pages/admin/AdminBrandAssets"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminTaxonomy = lazy(() => import("@/pages/admin/AdminTaxonomy"));
const AdminMenuSettings = lazy(() => import("@/pages/admin/AdminMenuSettings"));
const AdminSEO = lazy(() => import("@/pages/admin/AdminSEO"));
const AdminLegalPages = lazy(() => import("@/pages/admin/AdminLegalPages"));
const AdminWishlistAnalytics = lazy(() => import("@/pages/admin/AdminWishlistAnalytics"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminTryOn = lazy(() => import("@/pages/admin/AdminTryOn"));
const AdminTryOnLanding = lazy(() => import("@/pages/admin/AdminTryOnLanding"));
const AdminFooterSettings = lazy(() => import("@/pages/admin/AdminFooterSettings"));
const AccountProfile = lazy(() => import("@/pages/AccountProfile"));
const AccountAddresses = lazy(() => import("@/pages/AccountAddresses"));
const AccountReferrals = lazy(() => import("@/pages/AccountReferrals"));
const AccountTryOns = lazy(() => import("@/pages/AccountTryOns"));
const TryOnLanding = lazy(() => import("@/pages/TryOnLanding"));
const PollVote = lazy(() => import("@/pages/PollVote"));
const Invoice = lazy(() => import("@/pages/Invoice"));
const CoupleWear = lazy(() => import("@/pages/CoupleWear"));

const MyDesigns = lazy(() => import("@/pages/MyDesigns"));
const RequestReturn = lazy(() => import("@/pages/RequestReturn"));
const MyReturns = lazy(() => import("@/pages/MyReturns"));
const ReturnDetail = lazy(() => import("@/pages/ReturnDetail"));
const WishlistShare = lazy(() => import("@/pages/WishlistShare"));

// Tiny route-loading fallback — keeps layout stable while a chunk fetches.
function RouteFallback() {
  return (
    <div className="flex-1 flex items-center justify-center min-h-[40vh]" aria-label="Loading">
      <div className="w-10 h-10 rounded-full border-2 border-zinc-200 border-t-[#FF5722] animate-spin" />
    </div>
  );
}

// Mounts its children only after the browser is idle OR `delay` ms — whichever
// comes first. Used to keep heavy floating widgets (FeedbackWidget) out of the
// LCP/TBT budget on first paint.
function DeferredMount({ children, delay = 2000 }) {
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    const fire = () => { if (!cancelled) setReady(true); };
    let idleId = null;
    let timeoutId = null;
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(fire, { timeout: delay });
    } else {
      timeoutId = setTimeout(fire, delay);
    }
    return () => {
      cancelled = true;
      if (idleId && typeof window !== "undefined" && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [delay]);
  return ready ? children : null;
}

// Always scroll back to top on route change so users land at the header,
// not wherever they last scrolled on the previous page.
function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    // If the URL targets an in-page anchor, let the browser handle that.
    if (hash && hash !== "#") return;
    // Use double-rAF so DOM has painted the new route before scrolling.
    // We reset every plausible scroll container because `overflow-x: hidden`
    // on the root can move the scroll viewport off `window` on some browsers.
    const reset = () => {
      try { window.scrollTo(0, 0); } catch { /* ignore */ }
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      const root = document.getElementById("root");
      if (root) root.scrollTop = 0;
    };
    reset();
    const r1 = requestAnimationFrame(() => {
      reset();
      const r2 = requestAnimationFrame(reset);
      // Best-effort cleanup
      if (typeof window !== "undefined") window.__lastScrollRaf = r2;
    });
    return () => {
      cancelAnimationFrame(r1);
      if (typeof window !== "undefined" && window.__lastScrollRaf) cancelAnimationFrame(window.__lastScrollRaf);
    };
  }, [pathname, search, hash]);
  return null;
}

function AppShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isStudio = location.pathname.startsWith("/customize") || location.pathname === "/custom";
  const isCart = location.pathname === "/cart" || location.pathname.startsWith("/checkout");
  const isAuthCallback = location.hash?.includes("session_id=");
  const isAuthPage = ["/login", "/signup", "/forgot-password", "/reset-password"].includes(location.pathname);
  const isHome = location.pathname === "/";

  if (isAuthCallback) return <AuthCallback />;

  return (
    <div className="flex flex-col min-h-screen overflow-x-clip">
      <ScrollToTop />
      {!isAdmin && !isAuthPage && !isStudio && <Header />}
      {!isAdmin && !isAuthPage && !isStudio && <PendingCouponBanner />}
      <main className="flex-1 flex flex-col">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Catalog />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/p/:slug" element={<ProductDetail />} />
          <Route path="/custom" element={<CustomStudio />} />
          <Route path="/customize" element={<CustomStudio />} />
          <Route path="/customize/:slug" element={<CustomStudio />} />
          <Route path="/collections/:slug" element={<CollectionPage />} />
          <Route path="/heavy-duty" element={<HeavyDuty />} />
          <Route path="/share/:slug" element={<SharedDesign />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/register" element={<Register />} />
          <Route path="/refer/:code" element={<ReferralCapture />} />
          <Route path="/refer-and-earn" element={<ReferAndEarn />} />
          {/* Alias for marketing URLs / Indian e-commerce convention */}
          <Route path="/referrals" element={<ReferAndEarn />} />
          <Route path="/about" element={<About />} />
          <Route path="/custom-shop" element={<CustomShop />} />
          <Route path="/couple-wear" element={<CoupleWear />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/sell-your-designs" element={<SellYourDesigns />} />
          <Route path="/designers/leaderboard" element={<DesignerLeaderboard />} />
          <Route path="/leaderboard" element={<DesignerLeaderboard />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund-policy" element={<Refund />} />
          <Route path="/orders/:orderId/invoice" element={<ProtectedRoute><Invoice /></ProtectedRoute>} />

          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/order-success/:orderId" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
          <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
          {/* Public read-only shared wishlist (token-gated, no auth required) */}
          <Route path="/wishlist/shared/:token" element={<WishlistShare />} />
          <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path="/returns" element={<ProtectedRoute><MyReturns /></ProtectedRoute>} />
          <Route path="/returns/new/:orderId" element={<ProtectedRoute><RequestReturn /></ProtectedRoute>} />
          <Route path="/returns/:id" element={<ProtectedRoute><ReturnDetail /></ProtectedRoute>} />
          <Route path="/my-designs" element={<ProtectedRoute><MyDesigns /></ProtectedRoute>} />
          <Route path="/designer" element={<ProtectedRoute><DesignerDashboard /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/account/profile" element={<ProtectedRoute><AccountProfile /></ProtectedRoute>} />
          <Route path="/account/addresses" element={<ProtectedRoute><AccountAddresses /></ProtectedRoute>} />
          <Route path="/account/tryons" element={<ProtectedRoute><AccountTryOns /></ProtectedRoute>} />
          <Route path="/account/referrals" element={<ProtectedRoute><AccountReferrals /></ProtectedRoute>} />
          <Route path="/try-on" element={<TryOnLanding />} />
          <Route path="/poll/:slug" element={<PollVote />} />
          <Route path="/account/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

          <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="size-guides" element={<AdminSizeGuides />} />
            <Route path="heavy-duty" element={<AdminHeavyDuty />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="collections" element={<AdminCollections />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="design-library" element={<AdminDesignLibrary />} />
            <Route path="designers" element={<AdminDesigners />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="returns" element={<AdminReturns />} />
            <Route path="proofs" element={<AdminProofs />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="quote-requests" element={<AdminQuoteRequests />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="home-editor" element={<AdminHomeEditor />} />
            <Route path="header-nav" element={<AdminHeaderNav />} />
            <Route path="promo-banners" element={<AdminPromoBanners />} />
            <Route path="couple-inspirations" element={<AdminCoupleInspirations />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="couple-wear-page" element={<AdminCoupleWearPage />} />
            <Route path="email-templates" element={<AdminEmailTemplates />} />
            <Route path="abandoned-carts" element={<AdminAbandonedCarts />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="brand-assets" element={<AdminBrandAssets />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="taxonomy" element={<AdminTaxonomy />} />
            <Route path="menu-settings" element={<AdminMenuSettings />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="legal" element={<AdminLegalPages />} />
            <Route path="wishlist-analytics" element={<AdminWishlistAnalytics />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="tryon" element={<AdminTryOn />} />
            <Route path="tryon-landing" element={<AdminTryOnLanding />} />
            <Route path="footer-settings" element={<AdminFooterSettings />} />
            <Route path="orders/:orderId/invoice" element={<Invoice />} />
            <Route path="orders/:orderId/shipping-label" element={<AdminShippingLabel />} />
            <Route path="shipments" element={<AdminShipments />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          {/* Catch-all 404 — replaces blank page for any unknown URL */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      {!isAdmin && !isStudio && <Footer tight={isAuthPage || isHome} />}
      {/* Floating feedback widget — visible on every public page (excluded on
          admin, auth, Design Studio, Cart and Checkout so it doesn't clutter
          staff/login screens, overlap the studio toolbars, or distract users
          during the final purchase flow). Mounted via DeferredMount so it
          never blocks LCP. */}
      {!isAdmin && !isAuthPage && !isStudio && !isCart && (
        <DeferredMount delay={2500}>
          <Suspense fallback={null}>
            <FeedbackWidget />
          </Suspense>
        </DeferredMount>
      )}
      {/* Floating AI Try-On CTA — mobile + tablet only (lg:hidden inside the
          component). Skipped on /try-on itself (user already there), PDP
          (/p/:slug — the PDP already carries a prominent Try-On card above
          the colour swatches, no need to duplicate), studio/admin/auth
          surfaces to keep the workspace clean, and Cart/Checkout so it
          doesn't overlap the sticky "Proceed to Checkout" bottom bar. */}
      {!isAdmin && !isStudio && !isAuthPage && !isCart && location.pathname !== "/try-on" && !location.pathname.startsWith("/p/") && (
        <DeferredMount delay={1500}>
          <Suspense fallback={null}>
            <FloatingTryOnButton />
          </Suspense>
        </DeferredMount>
      )}
    </div>
  );
}

import { SiteSettingsProvider } from "@/components/SiteSettings";
import ErrorBoundary from "@/components/ErrorBoundary";

export default function App() {
  useEffect(() => {
    // Prevent the browser from restoring the previous scroll position when
    // navigating between routes. ScrollToTop always lands users at the header.
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      try { window.history.scrollRestoration = "manual"; } catch { /* ignore */ }
    }
  }, []);
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <SiteSettingsProvider>
            <CartProvider>
              <TaxonomyProvider>
                <GenderProvider>
                  <AppShell />
                  <Toaster richColors position="bottom-right" />
                </GenderProvider>
              </TaxonomyProvider>
            </CartProvider>
          </SiteSettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
